import { createStitches } from "@stitches/react";
import type * as Stitches from "@stitches/react";

function arrayToThemeObject<T extends string | number>(
  array: T[],
  unit = "px"
) {
  return array.reduce(
    (result, current, index) => ({ ...result, [index]: `${current}${unit}` }),
    {} as { [key: number]: string }
  );
}

const colors = {
  white: "hsl(0, 0%, 100%)",
  black: "hsl(0, 0%, 0%)",
  grey50: "hsl(0, 0%, 98%)",
  grey100: "hsl(0, 19%, 94%)",
  grey200: "hsl(0, 0%, 76.9%)",
  grey300: "hsl(240, 6%, 44%)",
  grey400: "hsl(120, 2%, 35%)",
  grey500: "hsl(120, 9%, 35%)",
  grey600: "hsl(120, 31%, 12%)",
  grey700: "hsl(120, 33%, 10%)",
  grey800: "hsl(120, 32%, 8%)",
  grey900: "hsl(83, 12%, 11%)",
  secondary: "hsl(262, 22.4%, 26.3%)",
  lightgreen: "hsl(86, 14%, 96%)",
  iris: "hsl(239, 82%, 81%)",
  error: "hsl(0, 87%, 63%)",
  green: "hsl(83, 34%, 35%)",
  green2: "hsl(119, 34%, 35%)",
  yellow: "hsl(51, 71%, 58%)",
  blue: "hsl(178, 33%, 50%)",
  primary: "hsl(119, 33%, 50%)",
  primaryHover: "$green2",
  primaryActive: "$green2",
  text: "$secondary",
  background: "$white",
  muted: "$grey500",
  placeholder: "$grey300",
};

const pxSpaces = [0, 4, 8, 16, 24, 32, 40, 48, 56, 72, 80, 96, 120];
//       index    0  1  2  3   4   5   6   7   8   9   10  11  12
const space = arrayToThemeObject(pxSpaces);

const pxRadii = [0, 8, 12, 16];
//      index    0  1  2   3
const radii = { ...arrayToThemeObject(pxRadii), circle: "50%" };

const shadows = {
  cards: "0px 4px 4px rgba(0, 0, 0, 0.25)",
  contact: "0px 4px 16px rgba(63, 52, 82, 0.16)",
};

// prettier-ignore
const fontSizes = {
  /* 12px */ 1: "0.75rem",
  /* 14px */ 2: "0.875rem", 
  /* 16px */ 3: "1rem" ,
  /* 18px */ 4: "1.125rem",
  /* 20px */ 5: "1.25rem", 
  /* 24px */ 6: "1.5rem", 
  /* 32px */ 7: "2rem" ,
  /* 40px */ 8: "2.5rem",
  /* 48px */ 9: "3rem"
}

const fontWeights = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  heavy: 900,
};

export const {
  styled,
  css,
  globalCss,
  keyframes,
  getCssText,
  theme,
  createTheme,
  config,
  reset,
} = createStitches({
  theme: {
    colors,
    space,
    radii,
    shadows,
    fontSizes,
    fontWeights,
    fonts: {
      body: '"Mulish", sans-serif',
      monospace: "Menlo, monospace",
    },
  },
  media: {
    bp1: "(min-width: 375px)",
    bp2: "(min-width: 768px)",
    bp3: "(min-width: 1280px)",
    bp4: "(min-width: 1440px)",
    mobile: "(max-width: 767px)",
  },
  utils: {
    marginX: (value: Stitches.PropertyValue<"marginLeft">) => ({
      marginLeft: value,
      marginRight: value,
    }),
    marginY: (value: Stitches.PropertyValue<"marginTop">) => ({
      marginTop: value,
      marginBottom: value,
    }),
    paddingX: (value: Stitches.PropertyValue<"paddingLeft">) => ({
      paddingLeft: value,
      paddingRight: value,
    }),
    paddingY: (value: Stitches.PropertyValue<"paddingTop">) => ({
      paddingTop: value,
      paddingBottom: value,
    }),
  },
});

// Use `Stitches.CSS` or the configured type as shown above
// type MyButtonProps = { css?: Stitches.CSS };
export type CSS = Stitches.CSS<typeof config>;
